import { AblyProvider as AblyReactProvider } from "ably/react";
import { type ReactNode, useEffect, useMemo, useRef } from "react";
import type { AblyLogger } from "../core";
import { configureAbly, destroyClient, getRuntimeConfig, initializeClient } from "../core";

type ExternalAblyClient = React.ComponentProps<typeof AblyReactProvider>["client"];

type BaseAblyProviderProps = {
	children: ReactNode;
	logger?: AblyLogger;
};

type ManagedAblyProviderProps = BaseAblyProviderProps & {
	apiKey: string;
	clientId?: string;
	client?: never;
};

type ExternalAblyProviderProps = BaseAblyProviderProps & {
	client: ExternalAblyClient;
	apiKey?: never;
	clientId?: never;
};

type AblyProviderProps = ManagedAblyProviderProps | ExternalAblyProviderProps;

export type { AblyProviderProps };

export default function Provider(props: AblyProviderProps) {
	const { children, logger: providedLogger } = props;

	if (providedLogger && getRuntimeConfig().logger !== providedLogger) {
		configureAbly({ logger: providedLogger });
	}

	const logger = providedLogger ?? getRuntimeConfig().logger;
	const managedApiKey = "apiKey" in props ? props.apiKey : undefined;
	const managedClientId = "clientId" in props ? props.clientId : undefined;
	const externalClient = "client" in props ? props.client : undefined;
	const isManagedClient = !externalClient;

	const client = useMemo(() => {
		if (externalClient) {
			return externalClient;
		}

		return initializeClient({ key: managedApiKey as string, clientId: managedClientId });
	}, [externalClient, managedApiKey, managedClientId]);

	const disconnectedAt = useRef<number | null>(null);
	useEffect(() => {
		client?.connection.on((stateChange) => {
			const { current, previous, reason, retryIn } = stateChange;

			if (current === "disconnected" || current === "suspended") {
				if (!disconnectedAt.current) {
					disconnectedAt.current = Date.now();
				}
				logger.warning("INFO", `Ably connection ${current}`, {
					reason: reason?.message,
					retryIn,
				});
			} else if (current === "connected") {
				if (disconnectedAt.current) {
					const downtimeMs = Date.now() - disconnectedAt.current;
					logger.info("INFO", "Ably connection recovered", {
						downtime_ms: downtimeMs,
						previous_state: previous,
					});
					disconnectedAt.current = null;
				} else {
					logger.info("INFO", "Ably connection established", { previous });
				}
			} else if (current === "failed") {
				logger.warning("INFO", "Ably connection failed", {
					reason: reason?.message,
				});
			}
		});

		return () => {
			client?.connection.off();
			if (isManagedClient) {
				destroyClient();
			}
		};
	}, [client, isManagedClient, logger]);

	return <AblyReactProvider client={client}>{children}</AblyReactProvider>;
}
